"use client";

import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, LogIn, LogOut, User, Mail } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { hasUserData } from "@/contexts/UserPreferencesContext";
import { useFavourites } from "@/contexts/FavouritesContext";
import Swal from "sweetalert2";
import { cn } from "@/lib/helpers/utils";
import { clearClientAuthState } from "@/lib/auth-client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useRef, useState } from "react";
import {
  logActivity,
  flushActivityLogs,
  clearLocalActivityLogs,
} from "@/lib/client-activity-log";

/**
 * Firebase error codes that mean the project itself is not set up — no
 * Authentication provisioned, the provider not enabled, or the host this app
 * is served from missing from the console's authorized-domains list.
 *
 * Worth separating from transient failures: telling someone to "try again"
 * when the server is misconfigured sends them round a loop that cannot
 * succeed, and hides the real problem from whoever could fix it.
 */
function isConfigurationError(code?: string): boolean {
  return (
    code === "auth/configuration-not-found" ||
    code === "auth/operation-not-allowed" ||
    // The deployed hostname is not on the Authorized domains list. Easy to hit
    // on a new environment, and no amount of retrying fixes it.
    code === "auth/unauthorized-domain" ||
    code === "auth/invalid-api-key" ||
    code === "auth/api-key-not-valid"
  );
}

type AuthAction = "sign-in" | "sign-out" | null;
type EmailStep = "email" | "otp";

export function UserAuth({ minimal = false }: { minimal?: boolean }) {
  const {
    user: authUser,
    status,
    signInWithGoogle,
    signInWithOtp,
    signOut,
  } = useAuth();
  const { translate } = useLanguage();
  const { preferences, clearPreferences } = useUserPreferences();
  const { favourites, clearFavourites } = useFavourites();
  const [authAction, setAuthAction] = useState<AuthAction>(null);

  // Email/OTP state
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailStep, setEmailStep] = useState<EmailStep>("email");
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  // Optional sign-up details. Collected here so a user who signs up by email
  // is not prompted again straight after logging in.
  const [institution, setInstitution] = useState("");
  const [country, setCountry] = useState("");
  const otpInputRef = useRef<HTMLInputElement>(null);

  const waitForNextPaint = async () => {
    await new Promise<void>((resolve) => {
      window.setTimeout(() => resolve(), 0);
    });
  };

  const resetClientState = async () => {
    await Promise.all([clearPreferences(), clearFavourites()]);
    clearClientAuthState();
  };

  const resetClientStateLocalOnly = async () => {
    await Promise.all([clearPreferences(true), clearFavourites(true)]);
    clearClientAuthState();
  };

  const startSignIn = async (profileInput?: {
    institution: string;
    country: string;
  }) => {
    setAuthAction("sign-in");
    // Open Google's account chooser first, in the same tick as the click.
    // Anything awaited before this point (clearing local state, logging) costs
    // the browser's user-gesture credit, which both delays the popup and gets
    // it blocked outright in Safari and Firefox.
    const signInPromise = signInWithGoogle(profileInput);
    // Local-only: the user is still unauthenticated here, so there is nothing
    // in the database to clear, and after sign-in the contexts reload from it.
    void resetClientStateLocalOnly();
    logActivity("sign-in-started", "User clicked sign in with Google");
    try {
      await signInPromise;
      // AuthProvider picks up the new session; contexts reload from the
      // database on the resulting status change.
      setAuthAction(null);
    } catch (error) {
      console.error("Google sign-in failed:", error);
      setAuthAction(null);
      const code = (error as { code?: string })?.code;
      await Swal.fire({
        title: translate("sign-in"),
        text: isConfigurationError(code)
          ? translate("signInNotConfigured")
          : translate("signInFailedRetry"),
        icon: "error",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  const startSignOut = async () => {
    setAuthAction("sign-out");
    await waitForNextPaint();
    logActivity("sign-out-started", "User clicked sign out");
    await flushActivityLogs();
    await resetClientStateLocalOnly();
    clearLocalActivityLogs();
    await signOut();
    window.location.href = window.location.origin;
  };

  const handleSendOtp = async () => {
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError(translate("emailAddress"));
      return;
    }
    setEmailLoading(true);
    setEmailError("");
    try {
      const res = await fetch("/api/auth/otp/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) {
        setEmailError(data.error || "Failed to send code");
        return;
      }
      setEmailStep("otp");
      setOtpCode("");
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch {
      setEmailError("Network error. Please try again.");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) return;
    setEmailLoading(true);
    setEmailError("");
    try {
      await resetClientState();
      logActivity("sign-in-started", "User signing in with email/OTP");
      await signInWithOtp(email.trim().toLowerCase(), otpCode, {
        institution,
        country,
      });
      resetEmailForm();
    } catch (error) {
      setEmailError(
        error instanceof Error && error.message
          ? error.message
          : "Verification failed. Please try again."
      );
      setEmailLoading(false);
    }
  };

  const resetEmailForm = () => {
    setShowEmailForm(false);
    setEmailStep("email");
    setEmail("");
    setOtpCode("");
    setInstitution("");
    setCountry("");
    setEmailError("");
    setEmailLoading(false);
  };

  useEffect(() => {
    if (status === "authenticated") {
      void flushActivityLogs();
    }
  }, [status]);

  const authDialog = (
    <Dialog open={authAction === "sign-out"}>
      <DialogContent
        className="sm:max-w-md [&>button]:hidden"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="items-center text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
          <DialogTitle>{translate("signingOut")}</DialogTitle>
          <DialogDescription>{translate("signOutProgress")}</DialogDescription>
        </DialogHeader>
        <div className="overflow-hidden rounded-lg border bg-muted/30">
          <div className="h-2 w-full bg-muted">
            <div className="h-full w-1/2 animate-[auth-progress_1.2s_ease-in-out_infinite] rounded-full bg-primary" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (status === "loading") {
    return (
      <>
        {authDialog}
        <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
          <div className="animate-pulse">{translate('loading')}</div>
        </div>
      </>
    );
  }

  if (authUser) {
    return (
      <>
      {authDialog}
      <div className="space-y-2">
        <div className={cn("flex items-center gap-2 px-3 py-2 text-sm", minimal && "justify-center px-0")}>
          {authUser.image ? (
            <img
              src={authUser.image}
              alt={authUser.name || "User"}
              className="w-8 h-8 rounded-full"
              title={minimal ? authUser.name || "User" : undefined}
            />
          ) : (
            <User className="w-8 h-8 p-1 rounded-full bg-primary/10" />
          )}
          {!minimal && (
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate text-white">{authUser.name}</p>
              <p className="text-xs text-gray-300 break-all">
                {authUser.email}
              </p>
            </div>
          )}
        </div>
        {!minimal && (
          <Button
            onClick={async () => {
              // Check if user has any modified data
              // const hasData = hasUserData(preferences, favourites.length);            
              // if (!hasData) {
              //   // No data to worry about, sign out directly
              //   signOut();
              //   return;
              // }

              // Confirm before logging out with sweetalert2
              const result = await Swal.fire({
                title: translate('sign-out'),
                text: translate('signOutConfirm'),
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: translate('yes'),
                cancelButtonText: translate('no')
              });

              if (result.isConfirmed) {
                await startSignOut();
              }
            }}
            variant="outline"
            size="sm"
            className="w-full justify-start"
            disabled={authAction !== null}
          >
            {authAction === "sign-out" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <LogOut className="mr-2 h-4 w-4" />
            )}
            {translate('sign-out')}
          </Button>
        )}
      </div>
      </>
    );
  }

  if (minimal) {
    return (
      <>
        {authDialog}
        <Button
          onClick={() => startSignIn()}
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full transition-all duration-200 hover:scale-105"
          title={translate('signInWithGoogle')}
          disabled={authAction !== null}
        >
          {authAction === "sign-in" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <LogIn className="h-4 w-4" />
          )}
        </Button>
      </>
    );
  }

  const confirmAndSignIn = async (signInFn: () => Promise<void>) => {
    const hasData = hasUserData(preferences, favourites.length);
    if (!hasData) {
      await signInFn();
      return;
    }

    const hasSubjects = preferences.homeElectiveSubjects.length > 0;
    const hasScores = Object.keys(preferences.homeSubjectScores).length > 0;
    const hasFavourites = favourites.length > 0;
    const hasRecommendations = preferences.recommendedRecommendations?.length > 0;
    const hasLocalData = hasSubjects || hasScores || hasFavourites || hasRecommendations;

    if (!hasLocalData) {
      await signInFn();
      return;
    }

    const dataItems = [];
    if (hasSubjects) dataItems.push(`• ${preferences.homeElectiveSubjects.length} elective subject(s)`);
    if (hasScores) dataItems.push(`• Score data for ${Object.keys(preferences.homeSubjectScores).length} subject(s)`);
    if (hasFavourites) dataItems.push(`• ${favourites.length} favourite programme(s)`);
    if (hasRecommendations) dataItems.push(`• ${preferences.recommendedRecommendations.length} AI recommendation(s)`);

    const dataList = dataItems.join('\n');
    const result = await Swal.fire({
      title: translate('sign-in'),
      html: `<div style="text-align: left;">
        <p>${translate('signInConfirm')}</p>
        <br/>
        <strong>Your local data:</strong><br/>
        <pre style="font-size: 0.9em; margin-top: 8px;">${dataList}</pre>
      </div>`,
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: translate('yes'),
      cancelButtonText: translate('no')
    });

    if (result.isConfirmed) {
      await signInFn();
    }
  };

  /**
   * Sign-up / sign-in form, laid out to match the agreed sign-up screen: a
   * centred "Sign up" heading, bold labels above roomy rounded inputs, the
   * "(Optional)" marker carried in the label itself, and a required-fields
   * footnote. It is a dialog rather than a sidebar panel because the sidebar
   * is too narrow for that layout.
   *
   * School/institution and country are collected here so a user who signs up
   * by email is not asked for them again straight after logging in.
   */
  const fieldClass =
    "w-full rounded-2xl border border-slate-300 bg-background px-4 py-3 text-base text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-60";
  const labelClass = "block text-base font-bold text-slate-700";

  const signUpDialog = (
    <Dialog
      open={showEmailForm}
      onOpenChange={(open) => {
        if (!open && !emailLoading) resetEmailForm();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="pt-2 sm:pt-2">
          <DialogTitle className="text-center text-3xl font-extrabold tracking-tight text-slate-600">
            {translate("signUp")}
          </DialogTitle>
          {/* Also satisfies Radix's aria-describedby requirement for a dialog. */}
          <DialogDescription className="text-center">
            {emailStep === "email"
              ? translate("signUpDescription")
              : translate("signUpOtpDescription")}
          </DialogDescription>
        </DialogHeader>

        {emailStep === "email" ? (
          <div className="space-y-5">
            <div className="space-y-2 pb-3">
              <label htmlFor="signup-email" className={labelClass}>
                {translate("emailAddress")}{" "}
                <span className="text-rose-600">*</span>
              </label>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                className={fieldClass}
                disabled={emailLoading}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="signup-institution" className={labelClass}>
                {translate("schoolInstitution")} ({translate("optionalField")})
              </label>
              <input
                id="signup-institution"
                type="text"
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                placeholder={translate("schoolInstitutionPlaceholder")}
                className={fieldClass}
                disabled={emailLoading}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="signup-country" className={labelClass}>
                {translate("countryRegion")} ({translate("optionalField")})
              </label>
              <input
                id="signup-country"
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                placeholder={translate("countryRegionPlaceholder")}
                className={fieldClass}
                disabled={emailLoading}
              />
            </div>

            <p className="text-sm text-slate-500">
              <span className="text-rose-600">*</span>{" "}
              {translate("requiredFieldsNote")}
            </p>

            {emailError && (
              <p className="text-sm text-rose-600">{emailError}</p>
            )}

            <Button
              onClick={handleSendOtp}
              variant="default"
              className="w-full h-12 rounded-2xl text-base cursor-pointer"
              disabled={emailLoading || !email.trim()}
            >
              {emailLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {translate("sendingCode")}
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  {translate("sendCode")}
                </>
              )}
            </Button>

            <button
              onClick={async () => {
                // Carry the sign-up form's optional fields over, so a user who
                // filled them in is not asked for them again after signing in.
                const typedProfile = { institution, country };
                resetEmailForm();
                await confirmAndSignIn(() => startSignIn(typedProfile));
              }}
              className="w-full text-sm text-muted-foreground hover:text-primary cursor-pointer py-1"
              disabled={emailLoading}
            >
              {translate("orSignInWithGoogle")}
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            <p className="text-center text-sm text-slate-500">
              {translate("codeSentTo")}{" "}
              <strong className="text-slate-700 break-all">{email}</strong>
            </p>

            <div className="space-y-2">
              <label htmlFor="signup-otp" className={labelClass}>
                {translate("verificationCode")}{" "}
                <span className="text-rose-600">*</span>
              </label>
              <input
                id="signup-otp"
                ref={otpInputRef}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpCode}
                onChange={(e) => {
                  setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setEmailError("");
                }}
                onKeyDown={(e) =>
                  e.key === "Enter" && otpCode.length === 6 && handleVerifyOtp()
                }
                className={cn(
                  fieldClass,
                  "text-center text-2xl tracking-[0.4em] font-mono"
                )}
                disabled={emailLoading}
                autoFocus
              />
            </div>

            {emailError && (
              <p className="text-sm text-rose-600">{emailError}</p>
            )}

            <Button
              onClick={handleVerifyOtp}
              variant="default"
              className="w-full h-12 rounded-2xl text-base cursor-pointer"
              disabled={emailLoading || otpCode.length !== 6}
            >
              {emailLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {translate("verifying")}
                </>
              ) : (
                translate("verifyAndSignIn")
              )}
            </Button>

            <div className="flex items-center justify-between text-sm">
              <button
                onClick={() => {
                  setEmailStep("email");
                  setOtpCode("");
                  setEmailError("");
                }}
                className="text-muted-foreground hover:text-primary cursor-pointer py-1"
                disabled={emailLoading}
              >
                {translate("changeEmail")}
              </button>
              <button
                onClick={handleSendOtp}
                className="text-muted-foreground hover:text-primary cursor-pointer py-1"
                disabled={emailLoading}
              >
                {translate("resendCode")}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      {authDialog}
      {signUpDialog}
      <div className="space-y-2">
        <Button
          onClick={() => confirmAndSignIn(() => startSignIn())}
          variant="default"
          size="sm"
          className="w-full justify-start cursor-pointer transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]"
          disabled={authAction !== null}
        >
          {authAction === "sign-in" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <LogIn className="mr-2 h-4 w-4" />
          )}
          {translate('signInWithGoogle')}
        </Button>
        <button
          onClick={() => setShowEmailForm(true)}
          className="w-full text-xs text-muted-foreground hover:text-primary cursor-pointer py-1"
          disabled={authAction !== null}
        >
          {translate("orSignInWithEmail")}
        </button>
      </div>
    </>
  );
}
